/**
 * File: assets/basari-eye-test-confirmation.js
 * Purpose: Eye Test Booking Confirmation UI — read-only presentation.
 * Sprint: 1B.16D — Eye Test Booking Confirmation UI
 * Owner: Basari Theme / PDP
 * Dependencies: basari-eye-test-booking.js (EyeTestController API), drawer host mount
 *
 * Rules:
 *   - Never write directly to lensFlow.eyeTestBooking
 *   - Read selectedStore / selectedDate / selectedTime via EyeTestController.getState()
 *   - Close reuses drawer closeDrawer / onClose
 *   - Add to Calendar is UI placeholder only (no ICS / Google / Apple / deep links)
 *   - No booking API / cart / email / persistence
 */
(function (global) {
  'use strict';

  var BasariEyeTestConfirmation = global.BasariEyeTestConfirmation || {};
  var READY_ATTR = 'data-basari-eye-test-confirmation-ready';
  var STATUS_CLOSED = 'closed';
  var MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function setHidden(node, hidden) {
    if (!node) return;
    if (hidden) {
      node.setAttribute('hidden', '');
    } else {
      node.removeAttribute('hidden');
    }
  }

  function pad2(value) {
    var n = Number(value);
    return n < 10 ? '0' + n : String(n);
  }

  function parseISODate(iso) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!match) return null;
    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { year: year, month: month, day: day };
  }

  function ordinalSuffix(day) {
    var n = Number(day);
    if (!n) return '';
    var mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  /**
   * Format ISO YYYY-MM-DD → "25th October 2025" (Basari design).
   * @param {string} iso
   * @returns {string}
   */
  function formatDisplayDate(iso) {
    var parsed = parseISODate(iso);
    if (!parsed) return iso ? String(iso) : '';
    var monthName = MONTH_NAMES[parsed.month - 1] || '';
    return String(parsed.day) + ordinalSuffix(parsed.day) + ' ' + monthName + ' ' + parsed.year;
  }

  /**
   * Format HH:mm → "15:00 (3:00 pm)" (Basari design).
   * @param {string} time
   * @returns {string}
   */
  function formatDisplayTime(time) {
    var raw = String(time || '').trim();
    var match = /^(\d{1,2}):(\d{2})$/.exec(raw);
    if (!match) return raw;

    var hours24 = Number(match[1]);
    var minutes = match[2];
    if (hours24 < 0 || hours24 > 23) return raw;

    var period = hours24 >= 12 ? 'pm' : 'am';
    var hours12 = hours24 % 12;
    if (hours12 === 0) hours12 = 12;

    var twentyFour = pad2(hours24) + ':' + minutes;
    return twentyFour + ' (' + hours12 + ':' + minutes + ' ' + period + ')';
  }

  function statusLabel(status, labels) {
    var key = String(status || '').toLowerCase();
    if (key === STATUS_CLOSED) {
      return (labels && labels.closed) || 'Closed';
    }
    return (labels && labels.openNow) || 'Open Now';
  }

  function EyeTestConfirmationUI(root, options) {
    this.root = root;
    this.options = options || {};
    this.labels = this.options.labels || {};
    this.cache = {};
    this._bound = false;
    this._onClick = this.onClick.bind(this);
    this._noticeTimer = null;
  }

  EyeTestConfirmationUI.prototype.getController = function () {
    if (typeof this.options.getController === 'function') {
      return this.options.getController();
    }
    return this.options.controller || null;
  };

  EyeTestConfirmationUI.prototype.cacheSelectors = function () {
    var root = this.root;
    this.cache = {
      a11y: root.querySelector('[data-basari-lens-eye-test-a11y]'),
      storeName: root.querySelector('[data-basari-eye-test-confirmation-store-name]'),
      storeAddress: root.querySelector('[data-basari-eye-test-confirmation-store-address]'),
      status: root.querySelector('[data-basari-eye-test-confirmation-status]'),
      statusDot: root.querySelector('[data-basari-eye-test-confirmation-status-dot]'),
      closing: root.querySelector('[data-basari-eye-test-confirmation-closing]'),
      distance: root.querySelector('[data-basari-eye-test-confirmation-distance]'),
      date: root.querySelector('[data-basari-eye-test-confirmation-date]'),
      time: root.querySelector('[data-basari-eye-test-confirmation-time]'),
      calendarBtn: root.querySelector('[data-basari-eye-test-confirmation-calendar]'),
      closeBtn: root.querySelector('[data-basari-eye-test-confirmation-close]'),
      calendarNotice: root.querySelector('[data-basari-eye-test-confirmation-calendar-notice]')
    };
  };

  EyeTestConfirmationUI.prototype.bind = function () {
    if (this._bound || !this.root) return;
    this.root.addEventListener('click', this._onClick);
    this._bound = true;
  };

  EyeTestConfirmationUI.prototype.unbind = function () {
    if (!this._bound || !this.root) return;
    this.root.removeEventListener('click', this._onClick);
    this._bound = false;
  };

  EyeTestConfirmationUI.prototype.onClick = function (event) {
    var target = event.target;
    if (!target) return;

    if (target.closest && target.closest('[data-basari-eye-test-confirmation-calendar]')) {
      event.preventDefault();
      this.handleAddToCalendar();
      return;
    }

    if (target.closest && target.closest('[data-basari-eye-test-confirmation-close]')) {
      event.preventDefault();
      this.handleClose();
    }
  };

  EyeTestConfirmationUI.prototype.readState = function () {
    var controller = this.getController();
    if (!controller || typeof controller.getState !== 'function') return null;
    return controller.getState();
  };

  EyeTestConfirmationUI.prototype.hydrateFromState = function () {
    var state = this.readState();
    var store = state && state.selectedStore && isObject(state.selectedStore) ? state.selectedStore : null;
    var dateIso = state && state.selectedDate ? String(state.selectedDate) : '';
    var timeRaw = state && state.selectedTime ? String(state.selectedTime) : '';

    if (this.cache.storeName) {
      this.cache.storeName.textContent = store && store.name ? String(store.name) : '';
    }
    if (this.cache.storeAddress) {
      this.cache.storeAddress.textContent = store && store.address ? String(store.address) : '';
    }
    if (this.cache.status) {
      this.cache.status.textContent = store
        ? statusLabel(store.status, {
            openNow: this.labels.openNow,
            closed: this.labels.closed
          })
        : '';
    }
    if (this.cache.closing) {
      this.cache.closing.textContent = store && store.closingTime ? String(store.closingTime) : '';
    }
    if (this.cache.distance) {
      this.cache.distance.textContent = store && store.distance ? String(store.distance) : '';
    }
    if (this.cache.statusDot) {
      var isOpen = !!(store && String(store.status || '').toLowerCase() !== STATUS_CLOSED);
      this.cache.statusDot.classList.toggle('is-open', isOpen && !!store);
      this.cache.statusDot.classList.toggle('is-closed', !isOpen && !!store);
    }

    var statusSep = this.root.querySelector('.basari-eye-test-confirmation__status-sep');
    if (statusSep) {
      setHidden(statusSep, !(store && store.closingTime));
    }

    if (this.cache.date) {
      this.cache.date.textContent = formatDisplayDate(dateIso);
    }
    if (this.cache.time) {
      this.cache.time.textContent = formatDisplayTime(timeRaw);
    }

    this.announceSummary(store, dateIso, timeRaw);
  };

  EyeTestConfirmationUI.prototype.announceSummary = function (store, dateIso, timeRaw) {
    if (!this.cache.a11y) return;

    var parts = [];
    var heading = this.labels.heading || 'Booking Completed';
    parts.push(heading);

    if (store && store.name) {
      parts.push(String(store.name));
    }
    if (dateIso) {
      parts.push(formatDisplayDate(dateIso));
    }
    if (timeRaw) {
      parts.push(formatDisplayTime(timeRaw));
    }

    this.cache.a11y.textContent = parts.join('. ');
  };

  /**
   * UI-only placeholder — no ICS / calendar deep links / backend.
   */
  EyeTestConfirmationUI.prototype.handleAddToCalendar = function () {
    var message =
      this.labels.calendarNotice ||
      'Calendar integration will be available in a future sprint.';

    if (this.cache.calendarNotice) {
      this.cache.calendarNotice.textContent = message;
      setHidden(this.cache.calendarNotice, false);
    }

    if (this.cache.a11y) {
      this.cache.a11y.textContent = message;
    }

    if (this._noticeTimer) {
      global.clearTimeout(this._noticeTimer);
    }

    var self = this;
    this._noticeTimer = global.setTimeout(function () {
      setHidden(self.cache.calendarNotice, true);
      self._noticeTimer = null;
    }, 5000);
  };

  EyeTestConfirmationUI.prototype.handleClose = function () {
    if (typeof this.options.onClose === 'function') {
      this.options.onClose();
      return;
    }

    var controller = this.getController();
    if (controller && typeof controller.closeDrawer === 'function') {
      controller.closeDrawer();
    }
  };

  EyeTestConfirmationUI.prototype.refresh = function () {
    this.hydrateFromState();
    setHidden(this.cache.calendarNotice, true);
  };

  EyeTestConfirmationUI.prototype.destroy = function () {
    if (this._noticeTimer) {
      global.clearTimeout(this._noticeTimer);
      this._noticeTimer = null;
    }
    this.unbind();
    if (this.root) {
      this.root.removeAttribute(READY_ATTR);
    }
    this.cache = {};
  };

  /**
   * @param {HTMLElement} root
   * @param {Object} options
   * @returns {EyeTestConfirmationUI|null}
   */
  function mount(root, options) {
    if (!root) return null;

    if (root.getAttribute(READY_ATTR) === 'true' && root._basariEyeTestConfirmationUI) {
      root._basariEyeTestConfirmationUI.refresh();
      return root._basariEyeTestConfirmationUI;
    }

    var ui = new EyeTestConfirmationUI(root, options || {});
    ui.cacheSelectors();
    ui.bind();
    ui.refresh();
    root.setAttribute(READY_ATTR, 'true');
    root._basariEyeTestConfirmationUI = ui;
    return ui;
  }

  BasariEyeTestConfirmation.mount = mount;
  BasariEyeTestConfirmation.EyeTestConfirmationUI = EyeTestConfirmationUI;
  BasariEyeTestConfirmation.formatDisplayDate = formatDisplayDate;
  BasariEyeTestConfirmation.formatDisplayTime = formatDisplayTime;

  global.BasariEyeTestConfirmation = BasariEyeTestConfirmation;
})(typeof window !== 'undefined' ? window : this);
